import { useSessionStateOptional } from '@/session';
import type { TabType } from '@/session/types';
import { resolveEntityKind } from '@/lib/sync/entity-store';

// Re-export for backwards-compat — resolveEntityKind lives next to the
// entityStore now (see @sync/entityStore). External callers that imported
// it from '@/hooks/navigation' continue to work.
export { resolveEntityKind };

// ---------------------------------------------------------------------------
// Read hooks — derive active IDs from SessionState (the single source of truth)
// Safe to call outside SessionProvider (e.g. share mode) — returns null.
// ---------------------------------------------------------------------------

/**
 * Returns the active entity ID from the focused panel's SessionState.
 * Returns null if outside SessionProvider or the active tab is not an entity
 * (document / canvas — each carries its entity id as the tab id).
 */
export function useActiveEntityId(): string | null {
  const session = useSessionStateOptional();
  if (!session?.state) return null;
  const panel = session.state.panels[session.state.focusedPanelId];
  if (!panel) return null;
  const tab = panel.tabs.find((t) => t.id === panel.activeId);
  if (tab?.type === 'document' || tab?.type === 'canvas') return tab.id;
  return null;
}

/**
 * Returns the active collection ID from the focused panel's SessionState.
 * Returns null if outside SessionProvider or active tab is not a collection.
 */
export function useActiveCollectionId(): string | null {
  const session = useSessionStateOptional();
  if (!session?.state) return null;
  const panel = session.state.panels[session.state.focusedPanelId];
  if (!panel) return null;
  const tab = panel.tabs.find((t) => t.id === panel.activeId);
  if (tab?.type === 'collection') return tab.id;
  return null;
}

/**
 * Whether any panel currently holds an open conversation (assistant) tab.
 * Used to reconcile the assistant's `fullTab` mode: when the last conversation
 * tab closes, the floating bar should return.
 */
export function useHasConversationTab(): boolean {
  const session = useSessionStateOptional();
  if (!session?.state) return false;
  return Object.values(session.state.panels).some((panel) =>
    panel.tabs.some((t) => t.type === 'conversation'),
  );
}

/**
 * Whether the focused panel's ACTIVE tab is a conversation/chat. Used to hide
 * the floating assistant bar when the user is already looking at a chat tab.
 */
export function useActiveTabIsConversation(): boolean {
  const session = useSessionStateOptional();
  if (!session?.state) return false;
  const panel = session.state.panels[session.state.focusedPanelId];
  if (!panel) return false;
  const tab = panel.tabs.find((t) => t.id === panel.activeId);
  return tab?.type === 'conversation';
}

// ---------------------------------------------------------------------------
// Navigation hooks — pure SessionState wrappers
// Safe to call outside SessionProvider — returns no-op functions.
// ---------------------------------------------------------------------------

/** Open a named workspace view in the focused panel. No-op in share mode. */
export function useNavigateToWorkspaceView(): (view: 'home' | 'documents' | 'collections' | 'trash' | 'calendar') => void {
  const session = useSessionStateOptional();
  return (view: 'home' | 'documents' | 'collections' | 'trash' | 'calendar') => { session?.openTab(view, 'view'); };
}

// ---------------------------------------------------------------------------
// Entity-unified navigation helpers
// ---------------------------------------------------------------------------

// Derived from TabType — automatically stays in sync when new entity kinds are
// added to TabType. 'view' is excluded because views are navigational (home,
// documents, trash…) rather than entity-opening intents.
type EntityTabKind = Exclude<TabType, 'view'>;

/**
 * Maps a resolved entity kind to the TabType used by SessionState.
 *
 * Shared by useNavigateToEntity (navigation.ts) and the wsEntityRoute
 * beforeLoad handler (router.tsx) — single source of truth, no duplication.
 *
 * Defaults to 'document' for null (entity not yet bootstrapped) or any
 * unrecognised kind so that new kinds added later don't silently break
 * routing. The EntitySubscriber in router.tsx will self-heal once the
 * entity store bootstraps the real kind.
 */
export function kindToTabType(
  kind: EntityTabKind | null,
): TabType {
  switch (kind) {
    case 'collection': return 'collection';
    case 'recording': return 'recording';
    case 'canvas': return 'canvas';
    case 'space': return 'space';
    case 'conversation': return 'conversation';
    default: return 'document'; // covers null + 'document'
  }
}

/**
 * Open any entity in the focused panel. Resolves kind from the entity store.
 * No-op in share mode.
 *
 * When the kind cannot be resolved (entity not yet bootstrapped) the helper
 * defaults to 'document'. The EntitySubscriber in router.tsx watches
 * entityMeta / recordings / canvases / spaces Maps and corrects the tab
 * type via openTab() once the bootstrap lands the row, so cold deep-links
 * still self-heal — see router.tsx EntitySubscriber.
 *
 * Note: createDoc is synchronous — applyEvent populates entityStore before
 * the function returns, so navigateToEntity always sees the row without
 * needing a caller-supplied kind hint.
 */
export function useNavigateToEntity(): (entityId: string) => void {
  const session = useSessionStateOptional();
  return (entityId: string) => {
    if (!session) return;
    const kind = resolveEntityKind(entityId);
    session.openTab(entityId, kindToTabType(kind));
  };
}
